import React, { useState, useMemo, useCallback, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform, LayoutAnimation, ActivityIndicator } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { openMessageFile } from '@/constants/utils/files';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import IconButton from '@/components/Buttons/IconButton';
import MessageContent from '@/components/Messages/MessageContent';
import MessageThinkingContent from '@/components/Messages/MessageThinkingContent';
import MessageImages from '@/components/Messages/MessageImages';
import MessageFiles from '@/components/Messages/MessageFiles';
import { imagesNotCoveredByFiles } from '@/constants/utils/message_file_groups';
import MessageButtons from '@/components/Messages/MessageButtons';
import Tooltip from '@/components/Tooltip';
import { MESSAGE_LAYOUT } from '@/constants/config';
import { useModelStore, useAgentStore, useLightboxStore } from '@/store';
import { normalizeModelId, resolveAttributionModelIds } from '@/constants/utils/message';
import PropTypes from 'prop-types';

// Preview text extraction - strips markdown and truncates
const getPreviewText = (content, maxLength = 120) => {
  if (!content) return '';
  // Strip common markdown syntax and HTML tags
  const plain = content
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/```[\s\S]*?```/g, '[code]') // Replace code blocks
    .replace(/`[^`]+`/g, '[code]') // Replace inline code
    .replace(/[#*_~\[\]()>]/g, '') // Remove markdown chars
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
  return plain.length > maxLength 
    ? plain.slice(0, maxLength).trim() + '...' 
    : plain;
};

// Create styles
const createStyles = (colors, isExpanded, isXS) => StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      transition: 'all 0.2s ease',
    }),
  },
  containerHovered: {
    borderColor: colors.border_color_input,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: 48,
    gap: 10,
  },
  headerPressed: {
    opacity: 0.8,
  },
  modelLogo: {
    width: 24,
    height: 24,
    borderRadius: 4,
  },
  agentAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  headerTextContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modelName: {
    flex: 1,
  },
  preferenceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  preferredBadge: {
    backgroundColor: 'rgba(35, 200, 45, 0.15)',
  },
  rejectedBadge: {
    backgroundColor: 'rgba(255, 40, 40, 0.15)',
  },
  expandButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: colors.solid_color_secondary,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  streamingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.solid_color_secondary,
  },
  previewContainer: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  previewText: {
    lineHeight: 20,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border_color_secondary,
    marginHorizontal: 12,
  },
  expandedContent: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    gap: 4,
    flexWrap: 'wrap',
  },
  actionButton: {
    opacity: 0.7,
  },
  actionButtonHovered: {
    opacity: 1,
  },
  preferenceActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: 'auto',
  },
  preferButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  preferButtonActive: {
    backgroundColor: 'rgba(35, 200, 45, 0.15)',
  },
  rejectButtonActive: {
    backgroundColor: 'rgba(255, 40, 40, 0.15)',
  },
  errorContainer: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  errorBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(254, 0, 0, 0.1)',
  },
});

// Streaming indicator with animated dots
const StreamingIndicator = React.memo(({ colors }) => {
  return (
    <View style={[createStyles(colors, false, false).streamingIndicator]}>
      <Icon name="spinner" size={12} color={colors.text_color_secondary} />
      <Text size="xs" color="secondary">Generating...</Text>
    </View>
  );
});

StreamingIndicator.displayName = 'StreamingIndicator';

// Model/Agent header with logo
const ModelHeader = React.memo(({ 
  modelIds, 
  agentIds, 
  colors, 
  styles,
  isPreferred,
}) => {
  const getDeepModelById = useModelStore(state => state.getDeepModelById);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const modelId = normalizeModelId(modelIds?.[0]);
  const modelRecord = useModelStore(state => (modelId ? state.models[modelId] : null));
  const model = modelRecord && modelId ? getDeepModelById(modelId) : null;
  const agent = agentIds?.[0] ? getAgentById(agentIds[0]) : null;
  const item = model || agent;
  const isModel = !!model;

  if (!item) {
    return (
      <View style={styles.headerTextContainer}>
        <Text size="s" weight="medium" color="secondary">AI Response</Text>
      </View>
    );
  }

  return (
    <>
      <Image
        source={{ uri: isModel ? item.logoPath : item.avatarPath }}
        style={isModel ? styles.modelLogo : styles.agentAvatar}
        contentFit="cover"
      />
      <View style={styles.headerTextContainer}>
        <Text size="s" weight="medium" color="primary" numberOfLines={1} style={styles.modelName}>
          {item.name}
        </Text>
        {isPreferred && (
          <View style={[styles.preferenceIndicator, styles.preferredBadge]}>
            <Icon name="star" size={10} color="rgb(35, 200, 45)" />
            <Text size="xs" style={{ color: 'rgb(35, 200, 45)' }}>Best</Text>
          </View>
        )}
      </View>
    </>
  );
});

ModelHeader.displayName = 'ModelHeader';
ModelHeader.propTypes = {
  modelIds: PropTypes.arrayOf(PropTypes.string),
  agentIds: PropTypes.arrayOf(PropTypes.string),
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  isPreferred: PropTypes.bool,
};

// Action buttons for expanded state
const CardActions = React.memo(({
  colors,
  styles,
  onCopy,
  onSpeak,
  onBookmark,
  onExport,
  onPrefer,
  onReject,
  isPreferred,
  isBookmarked,
  showPreferenceActions,
}) => {
  const [hoveredAction, setHoveredAction] = useState(null);

  return (
    <View style={styles.actionsContainer}>
      {onCopy && (
        <Tooltip label="Copy">
          <IconButton
            icon="copy"
            iconSize={13}
            color={colors.text_color_secondary}
            style={[styles.actionButton, hoveredAction === 'copy' && styles.actionButtonHovered]}
            onPress={onCopy}
            size="small"
            onMouseEnter={() => setHoveredAction('copy')}
            onMouseLeave={() => setHoveredAction(null)}
          />
        </Tooltip>
      )}
      {onSpeak && (
        <Tooltip label="Speak">
          <IconButton
            icon="volume-high"
            iconSize={13}
            color={colors.text_color_secondary}
            style={[styles.actionButton, hoveredAction === 'speak' && styles.actionButtonHovered]}
            onPress={onSpeak}
            size="small"
            onMouseEnter={() => setHoveredAction('speak')}
            onMouseLeave={() => setHoveredAction(null)}
          />
        </Tooltip>
      )}
      {onBookmark && (
        <Tooltip label={isBookmarked ? "Remove bookmark" : "Bookmark"}>
          <IconButton
            icon="bookmark"
            iconSize={13}
            color={colors.text_color_secondary}
            style={[styles.actionButton, hoveredAction === 'bookmark' && styles.actionButtonHovered]}
            onPress={onBookmark}
            size="small"
            iconIsSolid={isBookmarked}
            onMouseEnter={() => setHoveredAction('bookmark')}
            onMouseLeave={() => setHoveredAction(null)}
          />
        </Tooltip>
      )}
      {onExport && (
        <Tooltip label="Download">
          <IconButton
            icon="arrow-down-to-line"
            iconSize={13}
            color={colors.text_color_secondary}
            style={[styles.actionButton, hoveredAction === 'export' && styles.actionButtonHovered]}
            onPress={() => onExport('plain')}
            size="small"
            onMouseEnter={() => setHoveredAction('export')}
            onMouseLeave={() => setHoveredAction(null)}
          />
        </Tooltip>
      )}
      
      {showPreferenceActions && (
        <View style={styles.preferenceActions}>
          {onPrefer && (
            <Tooltip label="I prefer this response">
              <Pressable
                style={[styles.preferButton, isPreferred && styles.preferButtonActive]}
                onPress={onPrefer}
              >
                <Icon 
                  name="check" 
                  size={12} 
                  color={isPreferred ? 'rgb(35, 200, 45)' : colors.text_color_secondary} 
                />
                {!isPreferred && <Text size="xs" color="secondary">Prefer</Text>}
              </Pressable>
            </Tooltip>
          )}
          {onReject && !isPreferred && (
            <Tooltip label="I don't like this response">
              <Pressable
                style={styles.preferButton}
                onPress={onReject}
              >
                <Icon 
                  name="xmark" 
                  size={12} 
                  color={colors.text_color_secondary} 
                />
              </Pressable>
            </Tooltip>
          )}
        </View>
      )}
    </View>
  );
});

CardActions.displayName = 'CardActions';

function MessageResponseCard({
  message,
  conversationId,
  isExpanded,
  onToggleExpand,
  onCopy,
  onSpeak,
  onBookmark,
  onExport,
  onPreferResponse,
  onRejectResponse,
  onOpenLightbox,
  onOpenFileEditor,
  isXS = false,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { openLightbox } = useLightboxStore(state => state.actions);
  
  const styles = useMemo(() => createStyles(colors, isExpanded, isXS), [colors, isExpanded, isXS]);
  
  const isUser = message.role === 'user';
  const attributionModelIds = useMemo(
    () => resolveAttributionModelIds(message, null, false),
    [message],
  );
  const isStreaming = message.isStreaming || message.isTyping || message.isWaiting;
  const isError = message.isError || message.hasError;
  // Only show "Best" badge if user has explicitly selected a preference
  const hasUserSelectedPreference = message.flags?.hasSelectedPreferenceLocally === true;
  const isPreferred = hasUserSelectedPreference && message.flags?.isGroupedResponsePreferred === 1;
  const isBookmarked = message.flags?.isSaved === true;
  const hasContent = !!message.content && message.content.trim().length > 0;
  const hasMedia = (message.images?.length > 0) || (message.files?.length > 0);
  
  const previewText = useMemo(() => getPreviewText(message.content), [message.content]);
  
  const handleToggle = useCallback(() => {
    if (isStreaming) return; // Don't allow toggle while streaming
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    onToggleExpand?.();
  }, [onToggleExpand, isStreaming]);

  const handleImageClick = useCallback((image) => {
    if (onOpenLightbox) {
      onOpenLightbox({ imageUrl: image.url, fileId: image.id });
    } else {
      openLightbox({ imageUrl: image.url, conversationId });
    }
  }, [onOpenLightbox, openLightbox, conversationId]);

  // Don't render user messages in card format
  if (isUser) return null;

  return (
    <View 
      style={[styles.container, isHovered && styles.containerHovered]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <Pressable 
        style={({ pressed }) => [styles.header, pressed && styles.headerPressed]}
        onPress={handleToggle}
        disabled={isStreaming}
        accessibilityRole="button"
        accessibilityLabel={isExpanded ? "Collapse response" : "Expand response"}
        accessibilityState={{ expanded: isExpanded }}
      >
        <ModelHeader
          modelIds={attributionModelIds}
          agentIds={message.agentIds}
          colors={colors}
          styles={styles}
          isPreferred={isPreferred}
        />
        
        {isStreaming ? (
          <StreamingIndicator colors={colors} />
        ) : (
          <Pressable style={styles.expandButton} onPress={handleToggle}>
            <Text size="xs" color="secondary">
              {isExpanded ? 'Collapse' : 'Expand'}
            </Text>
            <Icon 
              name={isExpanded ? 'chevron-up' : 'chevron-down'} 
              size={12} 
              color={colors.text_color_secondary}
            />
          </Pressable>
        )}
      </Pressable>

      {/* Error indicator */}
      {isError && !isExpanded && (
        <View style={styles.errorContainer}>
          <View style={styles.errorBadge}>
            <Icon name="circle-exclamation" size={12} color="#DC2626" />
            <Text size="xs" style={{ color: '#DC2626' }}>Error occurred</Text>
          </View>
        </View>
      )}

      {/* Preview text (collapsed state) */}
      {!isExpanded && !isError && hasContent && (
        <View style={styles.previewContainer}>
          <Text size="s" color="secondary" style={styles.previewText} numberOfLines={3}>
            {previewText}
          </Text>
        </View>
      )}

      {/* Expanded content */}
      {isExpanded && (
        <>
          <View style={styles.divider} />
          <View style={styles.expandedContent}>
            {/* Thinking content - hide for media generation (image models show their own loading UI) */}
            {!!message.thinkingContent && !message.isThinking && !message.isStreaming && !message.isWaiting && !message.isRequestingMedia && (
              <MessageThinkingContent
                conversationId={conversationId}
                messageId={message.id}
                thinkingContent={message.thinkingContent}
                thinkingDurationMs={message.thinkingDurationMs}
                thinkingStartTime={message.thinkingStartTime}
                isThinking={message.isThinking}
                inferenceStatus={message.inferenceStatus}
                inferenceStage={message.inferenceStage}
                messageLayout={MESSAGE_LAYOUT.WIDE}
              />
            )}
            
            {/* Main content */}
            <MessageContent
              conversationId={conversationId}
              messageId={message.id}
              content={message.content}
              citations={message.citations}
              messageLayout={MESSAGE_LAYOUT.WIDE}
              isUser={false}
              isStreaming={message.isStreaming}
              isWaiting={message.isRequestingMedia ? message.isWaiting : (message.isThinking || message.isWaiting)}
              isTyping={message.isTyping}
              isRequestingMedia={message.isRequestingMedia}
              modelIds={attributionModelIds}
              agentIds={message.agentIds}
              isDeleted={message.flags?.isDeleted}
              inferenceStatus={message.inferenceStatus}
              inferenceStage={message.inferenceStage}
              inferenceElapsedMs={message.inferenceElapsedMs}
            />
            
            {/* Images */}
            {message.images?.length > 0 && (
              <MessageImages
                conversationId={conversationId}
                images={imagesNotCoveredByFiles(message.images, message.files)}
                onImageClick={handleImageClick}
              />
            )}
            
            {/* Files */}
            {message.files?.length > 0 && (
              <MessageFiles
                files={message.files}
                isSmall={false}
                align="left"
                isEditable={false}
                onPress={(file) => openMessageFile(file, { onOpenLightbox, conversationId })}
              />
            )}

            {/* Artifact generation in-progress indicator: covers only the brief
                window after the assistant's text has streamed but before the
                attachment arrives. While the stream is still active, the body
                already shows ArtifactWaitingCard, so the stream flags must be
                clear here or the card shows two spinners at once. */}
            {(message.isRequestingMedia === 'document' || message.isRequestingMedia === 'presentation' || message.isRequestingMedia === 'spreadsheet') &&
              !message.files?.length && !message.isError &&
              !message.isStreaming && !message.isTyping && !message.isWaiting && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <ActivityIndicator size="small" color={colors.text_color_secondary} />
                <Text size="s" color="secondary">
                  {t(message.isRequestingMedia === 'presentation'
                    ? 'messages.creatingPresentation'
                    : (message.isRequestingMedia === 'spreadsheet' ? 'messages.creatingSpreadsheet' : 'messages.creatingPdf'))}
                </Text>
              </View>
            )}
            
            {/* Message buttons */}
            {message.buttons?.length > 0 && (
              <MessageButtons 
                conversationId={conversationId} 
                messageId={message.id} 
                buttons={message.buttons} 
              />
            )}
          </View>
          
          {/* Actions */}
          <CardActions
            colors={colors}
            styles={styles}
            onCopy={hasContent ? onCopy : null}
            onSpeak={hasContent ? onSpeak : null}
            onBookmark={onBookmark}
            onExport={hasContent ? onExport : null}
            onPrefer={onPreferResponse}
            onReject={onRejectResponse}
            isPreferred={isPreferred}
            isBookmarked={isBookmarked}
            showPreferenceActions={!!onPreferResponse || !!onRejectResponse}
          />
        </>
      )}
    </View>
  );
}

MessageResponseCard.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    content: PropTypes.string,
    modelIds: PropTypes.arrayOf(PropTypes.string),
    agentIds: PropTypes.arrayOf(PropTypes.string),
    parameters: PropTypes.object,
    isStreaming: PropTypes.bool,
    isTyping: PropTypes.bool,
    isWaiting: PropTypes.bool,
    isThinking: PropTypes.bool,
    isError: PropTypes.bool,
    hasError: PropTypes.bool,
    thinkingContent: PropTypes.string,
    thinkingDurationMs: PropTypes.number,
    thinkingStartTime: PropTypes.number,
    inferenceStatus: PropTypes.string,
    isRequestingMedia: PropTypes.string,
    images: PropTypes.array,
    files: PropTypes.array,
    buttons: PropTypes.array,
    citations: PropTypes.array,
    flags: PropTypes.shape({
      isGroupedResponsePreferred: PropTypes.number,
      isSaved: PropTypes.bool,
      isDeleted: PropTypes.bool,
    }),
  }).isRequired,
  conversationId: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  onCopy: PropTypes.func,
  onBookmark: PropTypes.func,
  onExport: PropTypes.func,
  onPreferResponse: PropTypes.func,
  onRejectResponse: PropTypes.func,
  onOpenLightbox: PropTypes.func,
  onOpenFileEditor: PropTypes.func,
  isXS: PropTypes.bool,
};

MessageResponseCard.displayName = 'MessageResponseCard';

export default React.memo(MessageResponseCard);
